// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Index.tsx
//  Dashboard configurável: grid de widgets com personalização por usuário
//  (arrastar/redimensionar/adicionar/remover) persistida no backend.
//  Resolução do layout: usuário → perfil → template padrão.
//  Plano: docs/roadmaps/PLANO_DASHBOARD_CONFIGURAVEL.md
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
// Subpath /legacy = API v1 (WidthProvider + props planas) mantida pela v2 do
// react-grid-layout. O entry raiz da v2 tem outra API e NÃO exporta WidthProvider.
import GridLayout, { WidthProvider, type LayoutItem } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import { Check, Pencil, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardLayout, useWidgetsOcultos } from "@/features/dashboard/hooks/useDashboardLayout";
import { widgetPorId } from "@/features/dashboard/catalogo";
import { DashboardEditor } from "@/features/dashboard/components/DashboardEditor";
import type { WidgetPos } from "@/features/dashboard/types";

const Grid = WidthProvider(GridLayout);

const Index = () => {
  const { widgets, carregando, salvar, restaurar } = useDashboardLayout();
  const ocultos = useWidgetsOcultos();

  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState<WidgetPos[]>([]);

  const iniciarEdicao = () => {
    setRascunho(widgets);
    setEditando(true);
  };

  const salvarEdicao = () =>
    salvar.mutate(
      { versao: 1, widgets: rascunho },
      { onSuccess: () => setEditando(false) },
    );

  const restaurarPadrao = () =>
    restaurar.mutate(undefined, { onSuccess: () => setEditando(false) });

  // Visualização: esconde widgets sem dado útil (ex.: alertas de estoque numa
  // empresa de serviço).
  const visiveis = widgets.filter((w) => !ocultos.has(w.id));

  const layoutRgl: LayoutItem[] = visiveis.map((w) => ({
    i: w.id, x: w.x, y: w.y, w: w.w, h: w.h,
  }));

  return (
    <div className="p-6 space-y-4 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Visão geral do seu negócio em tempo real</p>
        </div>

        {editando ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={restaurarPadrao} disabled={restaurar.isPending}>
              <RotateCcw className="h-4 w-4 mr-1" /> Restaurar padrão
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditando(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={salvarEdicao} disabled={salvar.isPending}>
              <Check className="h-4 w-4 mr-1" /> Salvar
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={iniciarEdicao}>
            <Pencil className="h-4 w-4 mr-1" /> Personalizar
          </Button>
        )}
      </div>

      {editando ? (
        <DashboardEditor widgets={rascunho} onChange={setRascunho} />
      ) : carregando ? (
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : visiveis.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          Nenhum widget no seu dashboard.{" "}
          <button className="underline" onClick={iniciarEdicao}>Personalizar</button>
        </div>
      ) : (
        <Grid
          layout={layoutRgl}
          cols={12}
          rowHeight={60}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable={false}
          isResizable={false}
          compactType="vertical"
        >
          {visiveis.map((w) => {
            const def = widgetPorId(w.id);
            if (!def) return null;
            const Comp = def.componente;
            return (
              <div key={w.id}>
                <Comp config={w.config} />
              </div>
            );
          })}
        </Grid>
      )}
    </div>
  );
};

export default Index;
